import React, { useEffect, useMemo, useState } from 'react';
import { App, Button, Col, Form, Input, Modal, Row, Select, Typography } from 'antd';
import { CloseCircleOutlined, PlusOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Divider, Upload } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  useUpdateExpenseMutation,
  useGetExpensePaymentMethodsQuery,
  useUploadExpenseAttachmentMutation,
} from '../../services/expensesApi';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatCurrencyLocalized } from '../../utils/currencyHelpers';
import { canManageFinanzas, isSupportUser } from '../../utils/roleHelpers';
import { compressImage, HEIC_HINT } from '../../utils/imageCompression';
import { ExpenseDto, UpdateExpenseRequest } from '../../types/expense';
import QuickAddPaymentMethodModal from './QuickAddPaymentMethodModal';
import ExpenseAttachmentsViewer from './ExpenseAttachmentsViewer';

const { Text } = Typography;

interface Props {
  expense: ExpenseDto | null;
  restaurantId: string;
  onClose: () => void;
  /** Called when the user clicks the "Cancelar gasto" shortcut inside the edit dialog. */
  onRequestCancel: (e: ExpenseDto) => void;
}

interface FormValues {
  vendor?: string;
  notes?: string;
  paymentMethodId?: string | null;
}

/**
 * MVP edit: Vendor, Notes, and PaymentMethodId are mutable (the last one was added with
 * the payment-methods feature — it's descriptive, doesn't touch cash). Other fields
 * render read-only for context. See docs/finanzas-module.md "Edición unificada y mínima".
 */
const ExpenseEditDialog: React.FC<Props> = ({ expense, restaurantId, onClose, onRequestCancel }) => {
  const [form] = Form.useForm<FormValues>();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  // AntD v5: use App.useApp() for message context — see ExpenseCancelDialog comment.
  const { message } = App.useApp();
  const [updateExpense, { isLoading: isSaving }] = useUpdateExpenseMutation();
  const [uploadAttachment] = useUploadExpenseAttachmentMutation();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const user = useSelector((state: RootState) => state.auth?.user);
  const canManage = canManageFinanzas(user?.role, isSupportUser(user?.email));

  // We need to include inactive methods here so a historical expense linked to a
  // soft-deleted method still shows its name in the dropdown — otherwise the SELECT
  // would render an unrecognized GUID and the user would think the data is broken.
  const { data: paymentMethods, isFetching: loadingPaymentMethods } =
    useGetExpensePaymentMethodsQuery(
      { restaurantId, includeInactive: true },
      { skip: !expense }
    );

  useEffect(() => {
    if (expense) {
      form.setFieldsValue({
        vendor: expense.vendor || undefined,
        notes: expense.notes || undefined,
        // Initialize with the current value. The form payload below ALWAYS includes this
        // field so the backend (which treats null as "clear") doesn't accidentally wipe
        // it just because the user didn't touch the dropdown.
        paymentMethodId: expense.paymentMethodId ?? undefined,
      });
    }
  }, [expense, form]);

  // Build dropdown options. Mark soft-deleted methods with "(inactivo)" so the user
  // understands why a previously-used method appears there. Active methods come first.
  //
  // Inactive options are DISABLED except the one currently linked to this expense. The
  // backend only allows preserving the existing inactive method (UpdateMutableAsync's
  // "no change" branch); switching to a DIFFERENT inactive method is rejected with 404.
  // Disabling those entries surfaces the constraint in the UI so the user can't make a
  // pick the backend will reject. The current value remains selectable so it shows up
  // in the SELECT and the user can preserve it (or change to an active method).
  const currentMethodId = expense?.paymentMethodId ?? null;
  const paymentMethodOptions = useMemo(() => {
    const list = paymentMethods || [];
    const active = list
      .filter((m) => m.isActive)
      .map((m) => ({ value: m.id, label: m.name }));
    const inactive = list
      .filter((m) => !m.isActive)
      .map((m) => ({
        value: m.id,
        label: `${m.name} (inactivo)`,
        disabled: m.id !== currentMethodId,
      }));
    return [...active, ...inactive];
  }, [paymentMethods, currentMethodId]);

  if (!expense) return null;

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // CRITICAL: backend treats `null` as "clear the payment method". Always include the
    // resolved value (current or new) — antd's Select with allowClear returns undefined
    // when cleared, which we explicitly translate to null to clear server-side, OR pass
    // through the chosen GUID. Either way the field is in the payload, so a no-touch
    // edit preserves whatever was there originally (initialized from expense above).
    const resolvedPaymentMethodId =
      values.paymentMethodId === undefined ? null : values.paymentMethodId;

    const payload: UpdateExpenseRequest = {
      vendor: values.vendor?.trim() || null,
      notes: values.notes?.trim() || null,
      paymentMethodId: resolvedPaymentMethodId,
    };

    try {
      await updateExpense({ restaurantId, id: expense.id, data: payload }).unwrap();
      message.success('Gasto actualizado');
      onClose();
    } catch (err: any) {
      const serverMessage = err?.data?.message;
      message.error(serverMessage || 'No se pudo actualizar el gasto.');
    }
  };

  /**
   * Sube un lote de comprobantes, en serie. Se llama desde beforeUpload — ver el
   * comentario del <Upload> para por qué no vive en onChange.
   */
  const handleAddAttachments = async (batch: File[]) => {
    if (!expense || uploading || batch.length === 0) return;

    setUploading(true);
    try {
      for (const raw of batch) {
        // Compresión en el cliente: por datos móviles y porque el backend no
        // decodifica HEIC. Ver utils/imageCompression.ts.
        const compressed = await compressImage(raw);
        if (!compressed.ok) {
          message.error(`${raw.name}: ${HEIC_HINT}`);
          continue; // un HEIC no aborta el resto del lote
        }
        try {
          await uploadAttachment({
            restaurantId,
            expenseId: expense.id,
            file: compressed.file,
          }).unwrap();
          message.success(`${raw.name} agregado`);
        } catch (err: any) {
          message.error(err?.data?.message || `No se pudo subir ${raw.name}.`);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title="Editar gasto"
      open={!!expense}
      onCancel={onClose}
      footer={
        <div className="fin-modal-footer">
          {/* Acción destructiva separada de Guardar, patrón estándar.
              Punto 3 de 7 del barrido: cancelar revierte caja, así que el Gerente
              no lo ve. Es el gemelo del botón de la fila en ExpensesTab — este
              segundo sitio es el que un barrido hecho de memoria olvida. */}
          {canManage && (
            <Button
              danger
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={() => onRequestCancel(expense)}
              className="fin-modal-footer__lead"
              block={isMobile}
            >
              Cancelar gasto
            </Button>
          )}
          <Button onClick={onClose} block={isMobile}>
            Cerrar
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isSaving}
            disabled={isSaving}
            block={isMobile}
          >
            Guardar
          </Button>
        </div>
      }
      destroyOnClose
      // Ver ExpenseFormDialog: la geometría móvil del modal es global (App.css).
      width={560}
    >
      <div className="fin-summary">
        <span>
          <span className="fin-summary__label">Monto</span>
          <span className="fin-money">{formatCurrencyLocalized(expense.amount, 'MXN')}</span>
        </span>
        <span>
          <span className="fin-summary__label">Fecha</span>
          {formatInTimeZone(expense.expenseDate, 'DD MMM YYYY')}
        </span>
        <span>
          <span className="fin-summary__label">Categoría</span>
          {expense.categoryName}
        </span>
        <span>
          <span className="fin-summary__label">Caja</span>
          {expense.paidFromCash ? expense.cashRegisterUserName || '—' : 'sin caja'}
        </span>
      </div>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '-4px 0 12px' }}>
        Monto, fecha, categoría y caja no se editan: cancela este gasto y captura uno nuevo.
        Creado {formatInTimeZone(expense.createdAt, 'DD MMM HH:mm')}
        {expense.createdByUserName ? ` · ${expense.createdByUserName}` : ''}.
      </Text>

      <Form<FormValues> form={form} layout="vertical">
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="vendor" label="Proveedor">
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="paymentMethodId" label="Método de pago" style={{ marginBottom: 4 }}>
              <Select
                placeholder="Selecciona un método"
                allowClear
                options={paymentMethodOptions}
                loading={loadingPaymentMethods}
              />
            </Form.Item>
            {/* PUNTO 7 de 7 del barrido — el que el review encontró faltando.
                Este bloque está duplicado con ExpenseFormDialog (decisión consciente
                del spec de métodos de pago, por consistencia de UX). El costo de esa
                duplicación se cobra justo aquí: una regla de permisos nueva hay que
                aplicarla dos veces, y la segunda es invisible desde la primera.
                Sin esto, un Gerente abre el modal y se come un 403 al guardar. */}
            {canManage && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setQuickAddOpen(true)}
                style={{ paddingLeft: 0, marginBottom: 8 }}
              >
                Agregar nuevo método
              </Button>
            )}
          </Col>
        </Row>
        <Form.Item name="notes" label="Notas">
          <Input.TextArea maxLength={500} rows={3} showCount />
        </Form.Item>
      </Form>

      {/* Un lote a la vez: el guard de `uploading` evita que un segundo lote entre
          mientras el primero corre y se salte los topes del servidor. */}
      <Divider style={{ margin: '4px 0 12px' }} />

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Comprobantes
      </Text>
      <ExpenseAttachmentsViewer restaurantId={restaurantId} expenseId={expense.id} />

      {/* Agregar comprobantes siempre se permite, incluso sobre gastos cancelados:
          sirve para documentar por qué se canceló. Los adjuntos son inmutables —
          sólo se agregan, nunca se reemplazan. */}
      {/*
        fileList={[]} FIJO y el trabajo en beforeUpload, no en onChange.

        Con beforeUpload={() => false} y sin controlar fileList, AntD acumula: si el
        usuario agrega A y luego B, el segundo onChange trae A+B y A se sube DOS veces.
        Eso no sólo duplica el comprobante — consume el tope de 5 vivos y 15 históricos
        con basura, y muestra un "agregado" por cada subida como si todo hubiera ido
        bien. Es ensuciar datos reales, no un glitch visual.

        Con la lista siempre vacía, AntD nunca acumula nada. beforeUpload recibe el lote
        completo como segundo argumento, así que se procesa una sola vez (cuando el
        archivo es el primero del lote) y se devuelve false para que no suba solo.

        El form de creación no tiene este problema porque ahí fileList={attachments} es
        una lista de staging controlada que se sube una vez al guardar.
      */}
      <Upload
        multiple
        accept="image/*,application/pdf"
        showUploadList={false}
        fileList={[]}
        disabled={uploading}
        beforeUpload={(file: File, batch: File[]) => {
          // AntD invoca beforeUpload una vez por archivo con el lote completo. Sólo el
          // primero dispara el trabajo; los demás se ignoran para no procesar N veces.
          if (batch[0] === file) {
            void handleAddAttachments(batch);
          }
          return false;
        }}
      >
        <Button size="small" icon={<PaperClipOutlined />} loading={uploading} style={{ marginTop: 8 }}>
          Agregar comprobante
        </Button>
      </Upload>

      {canManage && (
        <QuickAddPaymentMethodModal
          open={quickAddOpen}
          restaurantId={restaurantId}
          onClose={() => setQuickAddOpen(false)}
          onCreated={(created) => {
            form.setFieldsValue({ paymentMethodId: created.id });
          }}
        />
      )}
    </Modal>
  );
};

export default ExpenseEditDialog;
